'use client';

import { useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Quote } from "lucide-react";
import Reveal from "@/components/ui/reveal";
import Image from "next/image";

export default function TestimonialCarousel() {
  const t = useTranslations("TestimonialsSection");
  const testimonialKeys = ["klara_krieg", "ferry_van_saalbach", "dionysios_satikidis", "heiner_lasi", "christian_malzacher", "bartosch_pliszek", "kerstin_hoefle", "dirk_slama", "martin_h_gladigow"] as const;
  const trackRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  function scrollByCards(direction: "left" | "right") {
    const el = trackRef.current;
    if (!el) return;
    const cardWidth = el.firstElementChild ? (el.firstElementChild as HTMLElement).offsetWidth : 360;
    const gap = 32;

    const newIndex = direction === "left"
      ? Math.max(0, activeIndex - 1)
      : Math.min(testimonialKeys.length - 1, activeIndex + 1);

    setActiveIndex(newIndex);
    el.scrollTo({
      left: newIndex * (cardWidth + gap),
      behavior: "smooth"
    });
  }

  return (
    <>
      <Reveal delay={100}>
        <div
          ref={trackRef}
          className="flex snap-x snap-mandatory gap-8 overflow-x-auto pb-8 [-ms-overflow-style:none] [scrollbar-width:none] px-8 group touch-pan-x py-4"
          style={{ scrollbarWidth: "none" }}
          aria-label="Testimonials carousel"
        >
          {testimonialKeys.map((key) => (
            <Card
              key={key}
              className="snap-center shrink-0 w-[95%] sm:w-[60%] lg:w-[45%] border-white/10 hover:border-primary/50 rounded-2xl border p-8 shadow-lg hover:shadow-2xl transition-all duration-500 hover:scale-[1.05] break-words hyphens-auto transform-gpu glass-strong glow-primary group/card relative overflow-hidden"
            >
              <div className="absolute inset-0 opacity-0 group-hover/card:opacity-100 transition-opacity duration-500 pointer-events-none shimmer" />

              <div className="flex flex-col h-full relative z-10">
                <div className="space-y-4 flex-grow relative">
                  <div className="absolute -left-4 -top-4 text-primary/10 p-2">
                    <Quote className="h-8 w-8" />
                  </div>
                  <p className="text-2xl font-bold text-gradient-brand leading-relaxed">
                    {t(`testimonials.${key}.pullQuote`)}
                  </p>
                  <p className="text-lg text-muted-foreground italic">
                    {t(`testimonials.${key}.text`)}
                  </p>
                </div>
                <div className="flex items-center gap-4 pt-3">
                  <div className="h-20 w-20 relative rounded-full overflow-hidden flex-shrink-0 border-2 border-primary/40 group-hover/card:border-primary group-hover/card:scale-110 transition-all duration-300">
                    <Image
                      src={`/images/testimonials/avt-${key}.webp`}
                      alt={t(`testimonials.${key}.name`)}
                      fill
                      sizes="(max-width: 768px) 96px, 96px"
                      className="object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-md font-semibold text-foreground">
                      {t(`testimonials.${key}.name`)}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      {t(`testimonials.${key}.title`)}
                    </span>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </Reveal>

      {/* Navigation Controls */}
      <div className="flex justify-center gap-4 mt-8 mb-4">
        <Button
          variant="outline"
          size="lg"
          className="border-border text-foreground bg-card hover:bg-accent rounded-full"
          aria-label={t("navigation.previous")}
          onClick={() => scrollByCards("left")}
          disabled={activeIndex === 0}
        >
          <ChevronLeft className="h-6 w-6" />
        </Button>
        <Button
          variant="outline"
          size="lg"
          className="border-border text-foreground bg-card hover:bg-accent rounded-full"
          aria-label={t("navigation.next")}
          onClick={() => scrollByCards("right")}
          disabled={activeIndex === testimonialKeys.length - 1}
        >
          <ChevronRight className="h-6 w-6" />
        </Button>
      </div>

      {/* Pagination Dots */}
      <div className="flex justify-center gap-1">
        {testimonialKeys.map((_, index) => (
          <button
            key={index}
            className="w-11 h-11 flex items-center justify-center rounded-full focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            onClick={() => {
              const el = trackRef.current;
              if (!el) return;
              const cardWidth = el.firstElementChild
                ? (el.firstElementChild as HTMLElement).offsetWidth
                : 360;
              const gap = 32;
              const scrollPosition = index * (cardWidth + gap);
              el.scrollTo({ left: scrollPosition, behavior: 'smooth' });
              setActiveIndex(index);
            }}
            aria-label={`Go to slide ${index + 1}`}
          >
            <span
              className={`block rounded-full transition-all duration-300 pointer-events-none ${index === activeIndex
                ? 'bg-primary w-6 h-3'
                : 'bg-muted-foreground hover:bg-primary/50 w-3 h-3'
                }`}
            />
          </button>
        ))}
      </div>
    </>
  );
}
